import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiClient } from '@/api/axios';
import { useBusiness } from './BusinessContext';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Membership {
  memberId: string;
  businessId: string;
  businessName: string;
  roleId: string;
  roleName: string;
  roleType: string;
  permissions: string[];
  locationIds: string[];
  isOwner: boolean;
}

interface PermissionContextType {
  memberships: Membership[];
  currentMembership: Membership | null;
  permissions: string[];
  isOwner: boolean;
  isLoading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  refetch: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(
  undefined
);

// ─── Provider ────────────────────────────────────────────────────────────────

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: isClerkLoaded, getToken } = useAuth();
  const { hasBusiness } = useBusiness();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const prevHasBusinessRef = useRef(false);

  // Helper: make authenticated GET using Clerk's getToken directly.
  // Throws "NO_TOKEN" if the session token is not yet available.
  const authGet = useCallback(
    async <T,>(url: string): Promise<T> => {
      let token: string | null = null;
      try {
        token = await getToken();
      } catch {
        // Clerk may throw if session isn't fully ready yet — treat as NO_TOKEN so retry kicks in
        throw new Error('NO_TOKEN');
      }
      if (!token) throw new Error('NO_TOKEN');
      const { data } = await apiClient.get<T>(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    },
    [getToken]
  );

  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 4;

  const fetchMembership = useCallback(async () => {
    // Wait for Clerk to fully load before making API calls
    if (!isClerkLoaded) return;

    if (!isSignedIn) {
      setMemberships([]);
      setIsLoading(false);
      retryCountRef.current = 0;
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await authGet<{ memberships: Membership[] }>(
        '/pro/me/membership'
      );
      setMemberships(data.memberships);
      retryCountRef.current = 0; // Reset on success
    } catch (err: any) {
      // Retry on any error (token not ready, network, 401, etc.) up to MAX_RETRIES
      if (retryCountRef.current < MAX_RETRIES) {
        const delay = err?.message === 'NO_TOKEN' ? 500 : 1000 * (retryCountRef.current + 1);
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => fetchMembership(), delay);
        return; // Don't clear isLoading — keep showing the loading state
      }
      console.error('Failed to fetch membership:', err);
      setError(err?.response?.data?.error || 'Failed to load membership');
      setMemberships([]);
    } finally {
      if (retryCountRef.current === 0 || retryCountRef.current >= MAX_RETRIES) {
        setIsLoading(false);
      }
    }
  }, [isSignedIn, isClerkLoaded, authGet]);

  // Initial fetch
  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  // Re-fetch when business becomes available (e.g. after auto-provisioning)
  useEffect(() => {
    if (hasBusiness && !prevHasBusinessRef.current) {
      fetchMembership();
    }
    prevHasBusinessRef.current = hasBusiness;
  }, [hasBusiness, fetchMembership]);

  // Use the membership matching the active business from BusinessContext
  const { businessId: activeBusinessId } = useBusiness();
  const currentMembership = memberships.find(m => m.businessId === activeBusinessId) ?? memberships[0] ?? null;
  const permissions = currentMembership?.permissions ?? [];
  const isOwner =
    currentMembership?.isOwner || permissions.includes('*');

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (isOwner || permissions.includes('*')) return true;
      return permissions.includes(permission);
    },
    [permissions, isOwner]
  );

  const hasAnyPermission = useCallback(
    (...requiredPermissions: string[]): boolean => {
      if (isOwner || permissions.includes('*')) return true;
      return requiredPermissions.some((p) => permissions.includes(p));
    },
    [permissions, isOwner]
  );

  const hasAllPermissions = useCallback(
    (...requiredPermissions: string[]): boolean => {
      if (isOwner || permissions.includes('*')) return true;
      return requiredPermissions.every((p) => permissions.includes(p));
    },
    [permissions, isOwner]
  );

  const value: PermissionContextType = {
    memberships,
    currentMembership,
    permissions,
    isOwner,
    isLoading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetch: fetchMembership,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePermissions(): PermissionContextType {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error(
      'usePermissions must be used within a PermissionProvider'
    );
  }
  return context;
}
